/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api-client';

// utils
import { formatDate, isNumber } from '@utils/functions';

// constants
import { NOT_APPLICABLE } from '@config/constants/common';

//types
import { TApiParams, TParams } from '@global-types/index';
import { TApiResponseGetMeasurabilities } from '../types';

import { useTranslation } from 'react-i18next';
import { getTechnologyProject } from '@utils/project-technologies';
import { getCountryName } from '@utils/countries';
import isNil from 'lodash/isNil';
import { formatNumberWithSpaces } from '@utils/formats';
import { getCurrencyUnit } from '@utils/currency-units';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getAdditionalities = (
	params: TApiParams
): Promise<TApiResponseGetMeasurabilities> => {
	return apiClient.get('/additionalities', {
		params: {
			page: params.page,
			pageSize: params.pageSize,
			populate:
				'cover, logo, publicSpecs, internalSpecs , entity , organization',
			query: params.queryParams,
			sort: params.sortParams,
		},
	});
};

export const useGetAdditionalities = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { t } = useTranslation();
	const { data, isFetching, isFetched } = useQuery({
		queryKey: [
			'databases-additionalities',
			{
				page: page,
				size: size,
				queryParams: queryParams,
				sortParams: sortParams,
			},
		],
		queryFn: () =>
			getAdditionalities({
				page: page,
				pageSize: size,
				queryParams: queryParams,
				sortParams: sortParams,
			}),
	});

	const updateList = (projects: any) =>
		projects.reduce((acc: any, currentItem: any) => {
			const technolgyProject = getTechnologyProject(
				currentItem.technology
			);
			const countryName = getCountryName(
				currentItem.location.countryCode
			);

			const unlimited =
				currentItem.financing &&
				currentItem.financing.unlimited
					? currentItem.financing.unlimited
					: 0;
			const financialNeeds = unlimited
				? 'Unlimited'
				: currentItem.financing &&
					  isNumber(currentItem.financing.target_euro)
					? currentItem.financing &&
						isNumber(currentItem.financing.target_euro)
						? currentItem.financing.target_euro
						: NOT_APPLICABLE
					: 0;
			const coverage =
				currentItem.financing &&
				isNumber(currentItem.financing.pledged_euro)
					? currentItem.financing.pledged_euro.toString()
					: 0;

			const percent =
				currentItem.financing &&
				isNumber(currentItem.financing.target_euro) &&
				isNumber(currentItem.financing.remaining_euro)
					? ((currentItem.financing.target_euro -
							currentItem.financing.remaining_euro) /
							currentItem.financing.target_euro) *
						100
					: 0;

			const progress = unlimited
				? 40
				: Math.round(percent);

			const cover =
				currentItem && currentItem.cover
					? currentItem.cover
					: technolgyProject
						? technolgyProject.image
						: '';

			const modifiedItem = {
				...currentItem,
				target_euro:
					!isNil(currentItem.financing) &&
					!isNil(currentItem.financing.target_euro)
						? formatNumberWithSpaces(
								currentItem?.financing.target_euro
							) +
							' ' +
							getCurrencyUnit('EUR')
						: 0 + ' ' + getCurrencyUnit('EUR'),
				pledged_euro:
					!isNil(currentItem.financing) &&
					!isNil(currentItem.financing.pledged_euro)
						? formatNumberWithSpaces(
								currentItem?.financing.pledged_euro
							) +
							' ' +
							getCurrencyUnit('EUR')
						: 0 + ' ' + getCurrencyUnit('EUR'),
				unlimited:
					!isNil(currentItem.financing) &&
					!isNil(currentItem.financing.unlimited) &&
					'' + currentItem.financing.unlimited,
				status:
					!isNil(currentItem.active) && currentItem.active
						? t('global.active')
						: t('global.inactive'),
				canceledBy:
					!isNil(currentItem.canceledBy) &&
					currentItem.canceledBy
						? currentItem.canceledBy
						: NOT_APPLICABLE,
				canceled:
					!isNil(currentItem.canceled) &&
					currentItem.canceled
						? t('global.canceled')
						: t('global.notCanceled'),
				canceledAt: !isNil(currentItem.canceledAt)
					? formatDate(
							currentItem.canceledAt,
							'DD/MM/YYYY'
						)
					: NOT_APPLICABLE,
				id: currentItem.id,
				name: currentItem.name,
				country: {
					name: countryName,
					code: currentItem.location.countryCode,
				},
				region: currentItem.location.place,
				type: technolgyProject
					? technolgyProject.label
					: '',
				image: cover,
				price: 0,
				financialNeeds: financialNeeds,
				coverage: coverage,
				remaining_euro: currentItem?.financing
					?.remaining_euro
					? currentItem?.financing.remaining_euro
					: 0,
				progress: progress,
				organization: !isNil(currentItem.organization)
					? currentItem.organization.name
					: NOT_APPLICABLE,
				createdAt: formatDate(
					currentItem.createdAt,
					'DD/MM/YYYY'
				),
				openingDate: !isNil(currentItem.openingDate)
					? formatDate(
							currentItem.openingDate,
							'DD/MM/YYYY'
						)
					: NOT_APPLICABLE,
				dueDate: !isNil(currentItem.dueDate)
					? formatDate(currentItem.dueDate, 'DD/MM/YYYY')
					: NOT_APPLICABLE,
				place:
					!isNil(currentItem.location) &&
					!isNil(currentItem.location.place)
						? currentItem.location.place
						: NOT_APPLICABLE,
				isDisabled:
					Number(financialNeeds) - Number(coverage) == 0,
				entity: !isNil(currentItem.entity)
					? currentItem.entity.name
					: NOT_APPLICABLE,
				technology:
					!isNil(currentItem.technology) &&
					!isNil(
						getTechnologyProject(currentItem.technology)
					) &&
					!isNil(
						getTechnologyProject(currentItem.technology)
							?.label
					)
						? t(
								'' +
									getTechnologyProject(
										currentItem.technology
									)?.label
							)
						: NOT_APPLICABLE,
			};
			acc.push(modifiedItem);

			return acc;
		}, []);

	return {
		data: data ? updateList(data?.rows) : [],
		isFetching,
		isFetched,
		total: data?.total ? data?.total : 0,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
